import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Stars } from "@/components/Badges";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LayoutGrid, Calendar, FileText, Gift, Star, Search, MapPin, Clock } from "lucide-react";

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutGrid },
  { key: "bookings", label: "My Bookings", icon: Calendar },
  { key: "quotes", label: "My Quotes", icon: FileText },
  { key: "referrals", label: "Refer & Earn", icon: Gift },
];

const STATUS_COLOR = {
  pending: "bg-gold-light text-gold-dark", scheduled: "bg-lawn-light text-lawn-dark",
  completed: "bg-lawn text-white", canceled: "bg-red-100 text-red-600",
  accepted: "bg-lawn-light text-lawn-dark", declined: "bg-red-100 text-red-600",
};

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const [active, setActive] = useState("overview");
  const [bookings, setBookings] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [refData, setRefData] = useState(null);
  const [reviewFor, setReviewFor] = useState(null);
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState("");

  const load = () => {
    api.get("/me/bookings").then((r) => setBookings(r.data.bookings)).catch(() => {});
    api.get("/me/quotes").then((r) => setQuotes(r.data.quotes)).catch(() => {});
    api.get("/me/referrals").then((r) => setRefData(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const submitReview = async () => {
    try {
      await api.post("/reviews", { booking_id: reviewFor.booking_id, provider_id: reviewFor.provider_id, rating, text: reviewText });
      toast.success("Review submitted! Thank you.");
      setReviewFor(null); setReviewText(""); setRating(5); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const upcoming = bookings.filter((b) => ["pending", "scheduled", "accepted"].includes(b.status)).length;
  const completed = bookings.filter((b) => b.status === "completed").length;

  return (
    <DashboardShell title="My Dashboard" nav={NAV} active={active} onNav={setActive} testId="customer-dashboard">
      {active === "overview" && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: "Upcoming", value: upcoming, icon: Calendar },
              { label: "Completed", value: completed, icon: Star },
              { label: "Open quotes", value: quotes.filter((q) => q.status !== "converted").length, icon: FileText },
              { label: "Credits", value: `$${refData?.credits ?? 0}`, icon: Gift },
            ].map((s, i) => (
              <div key={i} className="bg-white border border-border rounded-2xl p-5">
                <s.icon className="w-5 h-5 text-lawn mb-2" />
                <p className="font-display text-2xl font-extrabold text-lawn-ink">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            ))}
          </div>
          <div className="bg-lawn-ink rounded-2xl p-8 text-white flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="font-display text-2xl font-bold">Need lawn care?</h3>
              <p className="text-white/60 mt-1">Find and book a top-rated provider near you in minutes.</p>
            </div>
            <Button data-testid="dash-find-btn" onClick={() => navigate("/search")} className="rounded-full bg-lawn hover:bg-lawn-dark font-semibold gap-2 shrink-0"><Search className="w-4 h-4" /> Find Providers</Button>
          </div>
          <BookingList bookings={bookings.slice(0, 4)} onReview={setReviewFor} onView={navigate} />
        </div>
      )}

      {active === "bookings" && <BookingList bookings={bookings} onReview={setReviewFor} onView={navigate} full />}

      {active === "quotes" && (
        <div className="space-y-3">
          {quotes.length === 0 && <Empty text="No quote requests yet." />}
          {quotes.map((q) => (
            <div key={q.quote_id} className="bg-white border border-border rounded-2xl p-5 flex items-center justify-between">
              <div>
                <p className="font-semibold text-lawn-ink">{q.service_name} — {q.provider_name}</p>
                <p className="text-sm text-muted-foreground">{q.yard_size} · {q.frequency}</p>
              </div>
              <div className="text-right">
                {q.quote_price ? <p className="font-display text-xl font-extrabold text-lawn">${q.quote_price}</p> : <span className="text-xs text-muted-foreground">Awaiting quote</span>}
                <span className={`block text-[11px] px-2 py-0.5 rounded-full mt-1 ${STATUS_COLOR[q.status] || "bg-muted"}`}>{q.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {active === "referrals" && (
        <div className="bg-white border border-border rounded-2xl p-8 text-center">
          <Gift className="w-12 h-12 text-lawn mx-auto" />
          <h3 className="font-display text-2xl font-bold text-lawn-ink mt-4">Refer friends, earn credits</h3>
          <p className="text-muted-foreground mt-2">You've earned ${refData?.earned ?? 0} so far.</p>
          <Button data-testid="dash-referral-btn" onClick={() => navigate("/referral")} className="mt-6 rounded-full bg-lawn hover:bg-lawn-dark font-semibold">Open Referral Dashboard</Button>
        </div>
      )}

      <Dialog open={!!reviewFor} onOpenChange={(o) => !o && setReviewFor(null)}>
        <DialogContent data-testid="review-dialog">
          <DialogHeader><DialogTitle>Review {reviewFor?.provider_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Rating</Label>
              <div className="flex gap-1 mt-2">
                {[1,2,3,4,5].map((i) => <button key={i} data-testid={`review-star-${i}`} onClick={() => setRating(i)}><Star className={`w-8 h-8 ${i <= rating ? "fill-gold text-gold" : "text-gray-300"}`} /></button>)}
              </div>
            </div>
            <div><Label>Your review</Label><Textarea data-testid="review-text" value={reviewText} onChange={(e) => setReviewText(e.target.value)} className="mt-1.5 rounded-xl" placeholder="Share your experience…" /></div>
          </div>
          <DialogFooter><Button data-testid="review-submit" onClick={submitReview} className="rounded-full bg-lawn hover:bg-lawn-dark font-semibold">Submit Review</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}

const Empty = ({ text }) => <div className="bg-white border border-border rounded-2xl p-12 text-center text-muted-foreground">{text}</div>;

function BookingList({ bookings, onReview, onView, full }) {
  if (bookings.length === 0) return <Empty text="No bookings yet. Find a provider to get started!" />;
  return (
    <div className="space-y-3">
      {full && <h3 className="font-display text-lg font-bold text-lawn-ink">All bookings</h3>}
      {bookings.map((b) => (
        <div key={b.booking_id} data-testid={`booking-${b.booking_id}`} className="bg-white border border-border rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <p className="font-semibold text-lawn-ink">{b.service_name}</p>
            <p className="text-sm text-muted-foreground">{b.provider_name} · {new Date(b.scheduled_date).toLocaleDateString()} · {b.booking_type === "recurring" ? b.frequency : "one-time"}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-display font-extrabold text-lawn">${b.total_price}</span>
            <span className={`text-[11px] px-2.5 py-1 rounded-full font-semibold ${STATUS_COLOR[b.status] || "bg-muted"}`}>{b.status}</span>
            {b.status === "completed" && <Button data-testid={`review-btn-${b.booking_id}`} size="sm" variant="outline" className="rounded-full" onClick={() => onReview(b)}>Review</Button>}
          </div>
        </div>
      ))}
    </div>
  );
}
